/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import {
  Card,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Popover,
  Button,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useAuth } from "AuthContext";
import { filterByPermission, hasPermission } from "permissionUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SoftButton from "components/SoftButton";

const formatCompactDateRange = (range) => {
  if (!range?.start || !range?.end) return "--";

  const start = new Date(range.start);
  const end = new Date(range.end);

  const startDay = start.getDate();
  const endDay = end.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[end.getMonth()];
  const year = end.getFullYear();

  // If same month and year
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${startDay}–${endDay} ${month} ${year}`;
  }

  // If different months but same year
  if (start.getFullYear() === end.getFullYear()) {
    const startMonth = monthNames[start.getMonth()];
    return `${startDay} ${startMonth} – ${endDay} ${month} ${year}`;
  }

  // If different years
  return `${startDay} ${
    monthNames[start.getMonth()]
  } ${start.getFullYear()} – ${endDay} ${month} ${year}`;
};

const Header = ({
  dateRange,
  onPrev,
  onNext,
  activeView,
  setActiveView,
  activeDataTab,
  setActiveDataTab,
  isCheckedIn,
  activeOvertimeSubTab,
  setActiveOvertimeSubTab,
  selectedMonth,
  selectedYear,
  onDateRangeSelect, // Add this new prop for custom date range
}) => {
  const [timer, setTimer] = useState(0);
  const timerRef = useRef();
  const calendarRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorCal, setAnchorCal] = useState(null);
  const [tempDateRange, setTempDateRange] = useState({
    start: dayjs(dateRange?.start),
    end: dayjs(dateRange?.end),
  });
  const openMore = Boolean(anchorEl);
  const openCalendar = Boolean(anchorCal);

  const { userPermission } = useAuth();

  useEffect(() => {
    if (isCheckedIn) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isCheckedIn]);

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const newDate = new Date(selectedYear, selectedMonth, 1);
      calendarApi.gotoDate(newDate);
    }
  }, [selectedMonth, selectedYear]);

  // Update temp date range when props change
  useEffect(() => {
    if (dateRange?.start && dateRange?.end) {
      setTempDateRange({
        start: dayjs(dateRange.start),
        end: dayjs(dateRange.end),
      });
    }
  }, [dateRange]);

  const handleMoreClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenCalendar = (event) => setAnchorCal(event.currentTarget);

  const handleCloseCalendar = () => setAnchorCal(null);

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleApplyDateRange = () => {
    if (tempDateRange.start && tempDateRange.end) {
      // Ensure end date is after start date
      if (tempDateRange.start.isAfter(tempDateRange.end)) {
        // Swap if start is after end
        onDateRangeSelect({
          start: tempDateRange.end.format("YYYY-MM-DD"),
          end: tempDateRange.start.format("YYYY-MM-DD"),
        });
      } else {
        onDateRangeSelect({
          start: tempDateRange.start.format("YYYY-MM-DD"),
          end: tempDateRange.end.format("YYYY-MM-DD"),
        });
      }
      handleCloseCalendar();
    }
  };

  const handleResetDateRange = () => {
    const today = dayjs();
    const startOfWeek = today.startOf("week");
    const endOfWeek = today.endOf("week");

    setTempDateRange({
      start: startOfWeek,
      end: endOfWeek,
    });

    onDateRangeSelect({
      start: startOfWeek.format("YYYY-MM-DD"),
      end: endOfWeek.format("YYYY-MM-DD"),
    });
    handleCloseCalendar();
  };

  const iconStyle = (view) => ({
    color: activeView === view ? "info.main" : "#FFFFFF",
    cursor: "pointer",
    fontSize: "1.4rem",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "info.contrastText",
      transform: "scale(1.1)",
    },
  });

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const mainTabs = filterByPermission(
    [
      {
        label: "My Data",
        value: "mydata",
        permission: "Attendance.MyData",
      },
      {
        label: "Team",
        value: "team",
        permission: "Attendance.Team",
      },
      {
        label: "Overtime",
        value: "overtime",
        permission: "Attendance.Overtime",
      },
      {
        label: "Gap Reasons",
        value: "gapReasons",
        permission: "Attendance.GapReasons",
      },
      {
        label: "My Overtime",
        value: "myovertime",
        permission: "Attendance.MyOvertime",
      },
    ],
    userPermission
  );

  const overtimeSubTabs = filterByPermission(
    [
      {
        label: "Overtime Request",
        value: "request",
        permission: "Attendance.Overtime.Request",
      },
      {
        label: "Overtime History",
        value: "history",
        permission: "Attendance.Overtime.History",
      },
    ],
    userPermission
  );

  const getDefaultOvertimeSubTab = () => {
    if (overtimeSubTabs.length > 0) {
      if (hasPermission("Attendance.Overtime.Request", userPermission)) {
        return "request";
      }
      if (hasPermission("Attendance.Overtime.History", userPermission)) {
        return "history";
      }
    }
    return "";
  };

  useEffect(() => {
    if (mainTabs.length > 0) {
      const currentTabHasPermission = mainTabs.some((tab) => tab.value === activeDataTab);
      if (!activeDataTab || !currentTabHasPermission) {
        setActiveDataTab(mainTabs[0].value);
      }
    }
  }, [activeDataTab, mainTabs]);

  useEffect(() => {
    if (activeDataTab === "overtime" && !activeOvertimeSubTab && overtimeSubTabs.length > 0) {
      setActiveOvertimeSubTab(getDefaultOvertimeSubTab());
    }
  }, [activeDataTab, overtimeSubTabs, activeOvertimeSubTab]);
  return (
    <>
      {/* Calendar Date Range Picker Popover */}
      <Popover
        open={openCalendar}
        anchorEl={anchorCal}
        onClose={handleCloseCalendar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            background: "white",
            p: 3,
            width: isTablet ? "90vw" : 400,
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SoftTypography variant="h6" mb={1}>
            Select Date Range
          </SoftTypography>

          <SoftBox sx={{ mb: 1 }}>
            <SoftTypography variant="caption" color="textSecondary" mb={1}>
              From Date
            </SoftTypography>
            <DatePicker
              value={tempDateRange.start}
              onChange={(newValue) => setTempDateRange((prev) => ({ ...prev, start: newValue }))}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  sx: {
                    "& .MuiInputBase-root": {
                      borderRadius: "8px",
                    },
                  },
                },
              }}
            />
          </SoftBox>

          <SoftBox sx={{ mb: 1 }}>
            <SoftTypography variant="caption" color="textSecondary" mb={1}>
              To Date
            </SoftTypography>
            <DatePicker
              value={tempDateRange.end}
              onChange={(newValue) => setTempDateRange((prev) => ({ ...prev, end: newValue }))}
              minDate={tempDateRange.start}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  sx: {
                    "& .MuiInputBase-root": {
                      borderRadius: "8px",
                    },
                  },
                },
              }}
            />
          </SoftBox>

          <SoftBox sx={{ display: "flex", gap: 2 }}>
            <SoftButton
              variant="gradient"
              onClick={handleResetDateRange}
              fullWidth
              color="secondary"
            >
              This Week
            </SoftButton>
            <SoftButton variant="gradient" color="info" onClick={handleApplyDateRange} fullWidth>
              Apply
            </SoftButton>
          </SoftBox>
        </LocalizationProvider>
      </Popover>

      {/* 🔹 TOP HEADER (Main Tabs with permissions) */}
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(87deg, rgb(41, 88, 182) 0, rgb(40, 62, 152) 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 1,
          borderRadius: "10px",
          zIndex: 1000,
        }}
      >
        {/* LEFT – TABS */}
        <SoftBox display="flex" gap={3}>
          {mainTabs.map((tab) => (
            <SoftTypography
              key={tab.value}
              onClick={() => setActiveDataTab(tab.value)}
              variant="h6"
              sx={{
                color: activeDataTab === tab.value ? "#fff" : "rgba(255,255,255,.8)",
                borderBottom: activeDataTab === tab.value ? "2px solid #00c2ff" : "none",
                cursor: "pointer",
                pb: 0.5,
              }}
            >
              {tab.label}
            </SoftTypography>
          ))}
        </SoftBox>

        {/* RIGHT – FILTER ICONS (EXTREME RIGHT) */}
        {activeDataTab != "overtime" && activeDataTab != "myovertime" && activeDataTab != "gapReasons" && (
          <>
            {/* <SoftBox
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "8px 14px",
                border: "1px solid #dcdcdc",
                boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                minWidth: isTablet ? "220px" : "280px",
              }}
            >
              <Icon sx={{ cursor: "pointer" }} onClick={onPrev}>
                chevron_left
              </Icon>

              <Icon
                sx={{
                  color: "#344767",
                  cursor: "pointer",
                  backgroundColor: openCalendar ? "#E5E7EB" : "transparent",
                  borderRadius: "4px",
                  padding: "2px",
                }}
                onClick={handleOpenCalendar}
              >
                calendar_month
              </Icon>

              <Tooltip title={`${dateRange?.start || "--"} - ${dateRange?.end || "--"}`} arrow>
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  color="dark"
                  sx={{
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: 1,
                  }}
                >
                  {formatCompactDateRange(dateRange)}
                </SoftTypography>
              </Tooltip>

              <Icon sx={{ cursor: "pointer" }} onClick={onNext}>
                chevron_right
              </Icon>
            </SoftBox> */}
            <SoftBox
              ml="auto"
              sx={{
                borderRadius: "10px",
                // padding: "8px 12px",
                boxShadow: "0px 2px 6px rgba(22, 20, 20, 0.05)",
                display: "flex",
                gap: 1,
              }}
            >
              <Tooltip title="List View">
                <Icon sx={iconStyle("list")} onClick={() => setActiveView("list")}>
                  view_module
                </Icon>
              </Tooltip>

              <Tooltip title="Tabular View">
                <Icon sx={iconStyle("tabular")} onClick={() => setActiveView("tabular")}>
                  view_stream
                </Icon>
              </Tooltip>

              <Tooltip title="Calendar View">
                <Icon sx={iconStyle("calendar")} onClick={() => setActiveView("calendar")}>
                  calendar_month
                </Icon>
              </Tooltip>

              <Tooltip title="Filter Options">
                <Icon sx={iconStyle("filter")}>filter_list</Icon>
              </Tooltip>

              <Tooltip title="More">
                <Icon sx={iconStyle("more")} onClick={handleMoreClick}>
                  more_horiz
                </Icon>
              </Tooltip>

              <Menu anchorEl={anchorEl} open={openMore} onClose={handleMoreClose}>
                <MenuItem>
                  <Icon sx={{ mr: 1 }}>download</Icon> Import
                </MenuItem>
                <MenuItem>
                  <Icon sx={{ mr: 1 }}>upload</Icon> Export
                </MenuItem>
                <MenuItem>
                  <Icon sx={{ mr: 1 }}>visibility</Icon> Audit History
                </MenuItem>
              </Menu>
            </SoftBox>
          </>
        )}
      </Box>

      {/* 🔹 SUB TABS for Overtime (Overtime Request & Overtime History) */}
      {activeDataTab === "overtime" && overtimeSubTabs.length > 0 && (
        <Tabs
          sx={{ mt: 1, mb: 1.5 }}
          value={activeOvertimeSubTab}
          onChange={(e, v) => setActiveOvertimeSubTab(v)}
        >
          {overtimeSubTabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <SoftTypography fontSize="0.875rem" fontWeight="medium" color="text">
                  {tab.label}
                </SoftTypography>
              }
            />
          ))}
        </Tabs>
      )}
    </>
  );
};

export default Header;
